import { ReactNode } from "react";
import { PencilIcon, TrashIcon } from "lucide-react";
import { Button } from "@/components/UI/Buttons/Button";
import { cn } from "@/lib/utils";

interface TypeListProps<T> {
  types?: T[];
  onSelectType: (type: T) => void;
  onDeleteType: (type: T) => void;
  onEditType: (type: T) => void;
  title?: string;
  renderTypeContent?: (type: T) => ReactNode;
  renderTypeBadges?: (type: T) => ReactNode;
  extraActions?: (type: T) => ReactNode;
  getImage?: (type: T) => string | null | undefined;
  getDescription?: (type: T) => string | null | undefined;
  storageBaseUrl?: string;
}

function TypeList<T extends { id: number; name: string }>({
  types,
  onSelectType,
  onDeleteType,
  onEditType,
  title,
  renderTypeContent,
  renderTypeBadges,
  extraActions,
  getImage,
  getDescription,
  storageBaseUrl,
}: TypeListProps<T>) {
  const resolveImageUrl = (path: string) => {
    if (path.startsWith("http")) return path;
    return storageBaseUrl ? `${storageBaseUrl}/${path}` : path;
  };

  return (
    <div className="w-full min-w-0">
      <h4 className="text-ink mb-4 text-center font-semibold">
        {title || "Tipai"}
      </h4>

      <div className="flex flex-col gap-4">
        {types?.map((type) => {
          const imagePath = getImage?.(type);
          const description = getDescription?.(type);

          return (
            <div
              key={type.id}
              className={cn(
                "cursor-pointer rounded-xl border border-line-strong bg-paper p-4 transition-colors",
                "hover:bg-panel",
              )}
              onClick={() => onSelectType(type)}
            >
              <div className="flex items-center gap-4">
                {imagePath && (
                  <img
                    src={resolveImageUrl(imagePath)}
                    alt={type.name}
                    className="h-14 w-14 shrink-0 rounded-lg object-cover"
                  />
                )}

                <div className="min-w-0 shrink-0">
                  {renderTypeContent ? (
                    renderTypeContent(type)
                  ) : (
                    <div className="text-ink line-clamp-1 text-xl font-medium">
                      {type.name}
                    </div>
                  )}

                  {renderTypeBadges && (
                    <div className="mt-1">{renderTypeBadges(type)}</div>
                  )}
                </div>

                {description && (
                  <p className="text-muted min-w-0 flex-1 text-sm line-clamp-2">
                    {description}
                  </p>
                )}

                <div className="ml-auto flex shrink-0 items-center gap-2">
                  {extraActions && extraActions(type)}

                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditType(type);
                    }}
                  >
                    <PencilIcon />
                  </Button>

                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteType(type);
                    }}
                  >
                    <TrashIcon />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TypeList;
